<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <!-- GEO-LOCATION -->
    <template v-if="requiresLocation">
      <!-- LATITUDE -->
      <div class="form-group row">
        <form-label
          for-i-d="edit_site_latitude"
          :is-required="false"
          >{{ capitalize($t('names.latitude', { n: 1 })) }}
        </form-label>
        <div class="col-sm-8">
          <input
            id="edit_site_latitude"
            v-model="internalLatitude"
            name="latitude"
            :title="capitalize($t('names.latitude', { n: 1 }))"
            type="number"
            step="0.0001"
            :class="{ 'form-control': true, input: true, 'is-danger': $validator.messageBag.errors.has('latitude') }" />
          <span
            v-show="$validator.messageBag.errors.has('latitude')"
            class="help is-danger"
            >{{ $validator.messageBag.errors.first('latitude') }}</span
          >
        </div>
      </div>

      <!-- LONGITUDE -->
      <div class="form-group row">
        <!-- LONGITUDE -->
        <form-label
          for-i-d="edit_site_longitude"
          :is-required="false"
          >{{ capitalize($t('names.longitude', { n: 1 })) }}
        </form-label>
        <div class="col-sm-8">
          <input
            id="edit_site_longitude"
            v-model="internalLongitude"
            name="longitude"
            :title="capitalize($t('names.longitude', { n: 1 }))"
            type="number"
            step="0.0001"
            :class="{
              'form-control': true,
              input: true,
              'is-danger': $validator.messageBag.errors.has('longitude'),
            }" />
          <span
            v-show="$validator.messageBag.errors.has('longitude')"
            class="help is-danger"
            >{{ $validator.messageBag.errors.first('longitude') }}</span
          >
        </div>
      </div>

      <!-- MAP -->
      <div class="form-group row">
        <div class="col-12">
          <div class="coordinates-picker">
            <inventory-map
              :allow-mouse-scroll-zooming="true"
              :enable-map-point-control="true"
              :enable-map-style-control="true"
              :geocoded-locations="
                internalLatitude != null && internalLongitude != null
                  ? [
                      {
                        id: 0,
                        name: 'destination',
                        type: 'target',
                        lng: internalLongitude,
                        lat: internalLatitude,
                      },
                    ]
                  : []
              "
              :fit-to-bounds="true"
              @point-clicked="onMapPointClicked" />
          </div>
        </div>
      </div>
    </template>

    <!-- WEATHER CONDITIONS -->
    <template v-if="targetingCriteria.find((x) => x.type === weatherTargetingCriteriaType) != null">
      <div class="form-group row">
        <form-label>{{ capitalize($t('messages.targetWeatherConditions')) }}</form-label>
        <div class="col-sm-8 edit-weather-targeting-conditions-toggle">
          <toggle-button
            :color="colors.success"
            :value="enableWeatherConditions"
            :disabled="isReadOnly"
            :labels="{
              checked: capitalize(i18n.global.t('actions.yes')),
              unchecked: capitalize(i18n.global.t('actions.no')),
            }"
            :sync="true"
            data-entity-type="weather-condition"
            @click.stop.prevent=""
            @change="
              () => {
                enableWeatherConditions = !enableWeatherConditions;
              }
            " />
        </div>
      </div>

      <div
        v-show="enableWeatherConditions"
        class="offset-sm-3 col-sm-8 scale-vertical">
        <div class="mb-3">{{ messageForWeatherConditions }}</div>
        <div class="weather-conditions">
          <selectable-tiles
            ref="weatherConditionsSelectableTiles"
            :items="allWeathers"
            :is-read-only="isReadOnly"
            :should-display-labels="true"
            :should-display-icons="true"
            :should-display-checkmark-on-selected-items="true"
            label-property-name="name"
            icon-property-name="icon"
            :selected-items="internalSelectedWeatherConditions"
            :is-multiselect="true"
            :allow-empty="false"
            @selection-changed="onWeatherSelectionChanged" />
        </div>
      </div>
    </template>

    <!-- TEMPERATURE -->
    <template v-if="targetingCriteria.find((x) => x.type === temperatureTargetingCriteriaType) != null">
      <div class="form-group row">
        <form-label>{{ capitalize($t('messages.targetTemperature')) }}</form-label>
        <div class="col-sm-9 edit-weather-targeting-temperature-toggle">
          <toggle-button
            :color="colors.success"
            :value="enableWeatherTemperature"
            :disabled="isReadOnly"
            :labels="{
              checked: capitalize(i18n.global.t('actions.yes')),
              unchecked: capitalize(i18n.global.t('actions.no')),
            }"
            :sync="true"
            data-entity-type="target-temperature"
            @change="
              () => {
                enableWeatherTemperature = !enableWeatherTemperature;
              }
            " />
        </div>
      </div>

      <div
        v-show="enableWeatherTemperature"
        class="offset-sm-3 col-sm-9 scale-vertical">
        <div>
          <label>{{ messageForTemperatureMatches }}</label>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="temperature-controls-container">
              <div id="weather_temp_limit">
                <multiselect
                  v-model="selectedTempLimit"
                  track-by="id"
                  label="name"
                  :disabled="isReadOnly"
                  :options="allTemperatureLimits()"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="false"
                  :multiselect="false" />
              </div>
              <div id="weather_temp_comparator">
                <multiselect
                  v-model="selectedTempComparator"
                  track-by="id"
                  label="name"
                  :disabled="isReadOnly"
                  :options="allComparators()"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="false"
                  :multiselect="false" />
              </div>
              <div id="weather_temp_target">
                <formatted-input
                  name="targetTemperatureValue"
                  :readonly="isReadOnly"
                  :is-money="false"
                  :min="-130"
                  :max="135"
                  :value="internalTemperatureValue"
                  :validation-errors="$validator.messageBag.errors"
                  :suffix="tempUnit === 'C' ? '°C' : '°F'"
                  @value-changed="
                    (x) => {
                      const num = parseFloat(x);
                      if (!isNaN(num)) {
                        internalTemperatureValue = num;
                      }
                    }
                  " />
              </div>
              <div>
                <div class="btn-group">
                  <button
                    class="btn btn-md btn-default"
                    data-entity-type="celsius"
                    :disabled="isReadOnly"
                    :class="{ selected: tempUnit === 'C' }"
                    @click="changeTempUnit('C')">
                    {{ capitalize($t('messages.celsius')) }}
                  </button>
                  <button
                    class="btn btn-md btn-default"
                    data-entity-type="fahrenheit"
                    :disabled="isReadOnly"
                    :class="{ selected: tempUnit === 'F' }"
                    @click="changeTempUnit('F')">
                    {{ capitalize($t('messages.fahrenheit')) }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- SNOW -->
    <template v-if="targetingCriteria.find((x) => x.type === snowTargetingCriteriaType) != null">
      <div class="form-group row">
        <form-label>{{ capitalize($t('messages.targetSnowAccumulation')) }}</form-label>
        <div class="col-sm-9">
          <toggle-button
            id="edit_weather_targeting_snow_toggle"
            :color="colors.success"
            :value="enableSnowAccumulation"
            :disabled="isReadOnly"
            :labels="{
              checked: capitalize(i18n.global.t('actions.yes')),
              unchecked: capitalize(i18n.global.t('actions.no')),
            }"
            :sync="true"
            @change="
              () => {
                enableSnowAccumulation = !enableSnowAccumulation;
              }
            " />
        </div>
      </div>

      <div
        v-show="enableSnowAccumulation"
        class="offset-sm-3 col-sm-9 scale-vertical">
        <div>
          <label>{{ messageForSnowAccumulationMatches }}</label>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="accumulation-controls-container">
              <div id="snow_accumulation_comparator">
                <multiselect
                  v-model="selectedSnowAccumulationComparator"
                  track-by="id"
                  label="name"
                  :disabled="isReadOnly"
                  :options="allComparators()"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="false"
                  :multiselect="false" />
              </div>
              <div id="snow_accumulation_target">
                <formatted-input
                  name="targetSnowAccumulationValue"
                  :readonly="isReadOnly"
                  :is-money="false"
                  :min="-130"
                  :max="135"
                  :value="internalSnowAccumulationValue"
                  :validation-errors="$validator.messageBag.errors"
                  :suffix="snowAccumulationUnit === 'cm' ? 'cm' : 'in'"
                  @value-changed="
                    (x) => {
                      const num = parseFloat(x);
                      if (!isNaN(num)) {
                        internalSnowAccumulationValue = num;
                      }
                    }
                  " />
              </div>
              <div>
                <div class="btn-group">
                  <button
                    class="btn btn-md btn-default"
                    :disabled="isReadOnly"
                    :class="{ selected: snowAccumulationUnit === 'cm' }"
                    @click="changeSnowAccumulationUnit('cm')">
                    {{ 'centimeters' }}
                  </button>
                  <button
                    class="btn btn-md btn-default"
                    :disabled="isReadOnly"
                    :class="{ selected: snowAccumulationUnit === 'in' }"
                    @click="changeSnowAccumulationUnit('in')">
                    {{ capitalize('inches') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- RAIN -->
    <template v-if="targetingCriteria.find((x) => x.type === rainTargetingCriteriaType) != null">
      <div class="form-group row">
        <form-label>{{ capitalize($t('messages.targetRainAccumulation')) }}</form-label>
        <div class="col-sm-9">
          <toggle-button
            id="edit_weather_targeting_rain_toggle"
            :color="colors.success"
            :value="enableRainAccumulation"
            :disabled="isReadOnly"
            :labels="{
              checked: capitalize(i18n.global.t('actions.yes')),
              unchecked: capitalize(i18n.global.t('actions.no')),
            }"
            :sync="true"
            @change="
              () => {
                enableRainAccumulation = !enableRainAccumulation;
              }
            " />
        </div>
      </div>

      <div
        v-show="enableRainAccumulation"
        class="offset-sm-3 col-sm-9 scale-vertical">
        <div>
          <label>{{ messageForRainAccumulationMatches }}</label>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="accumulation-controls-container">
              <div id="rain_accumulation_comparator">
                <multiselect
                  v-model="selectedRainAccumulationComparator"
                  track-by="id"
                  label="name"
                  :disabled="isReadOnly"
                  :options="allComparators()"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="false"
                  :multiselect="false" />
              </div>
              <div id="rain_accumulation_target">
                <formatted-input
                  name="targetRainAccumulationValue"
                  :readonly="isReadOnly"
                  :is-money="false"
                  :min="-130"
                  :max="135"
                  :value="internalRainAccumulationValue"
                  :validation-errors="$validator.messageBag.errors"
                  :suffix="rainAccumulationUnit === 'mm' ? 'mm' : 'in'"
                  @value-changed="
                    (x) => {
                      const num = parseFloat(x);
                      if (!isNaN(num)) {
                        internalRainAccumulationValue = num;
                      }
                    }
                  " />
              </div>
              <div>
                <div class="btn-group">
                  <button
                    class="btn btn-md btn-default"
                    :disabled="isReadOnly"
                    :class="{ selected: rainAccumulationUnit === 'mm' }"
                    @click="changeRainAccumulationUnit('mm')">
                    {{ 'millimeters' }}
                  </button>
                  <button
                    class="btn btn-md btn-default"
                    :disabled="isReadOnly"
                    :class="{ selected: rainAccumulationUnit === 'in' }"
                    @click="changeRainAccumulationUnit('in')">
                    {{ capitalize('inches') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script type="text/babel">
  import { capitalize } from 'vue';

  import Colors from '@/Colors';
  import FormattedInput from '@/common/components/controls/FormattedInput';
  import FormLabel from '@/common/components/controls/FormLabel';
  import InventoryMap from '@/common/components/controls/InventoryMap';
  import SelectableTiles from '@/common/components/controls/SelectableTiles';
  import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
  import Creative from '@/common/models/creative/Creative';
  import LineItem from '@/common/models/lineitem/LineItem';
  import Package from '@/common/models/package/Package';
  import {
    celsiusToFahrenheit,
    centimetersToInches,
    fahrenheitToCelsius,
    inchesToCentimeters,
    inchesToMillimeters,
    millimetersToInches,
  } from '@/globals';
  import LocalCache from '@/utils/LocalCache';
  import i18n from '@/vueI18n';

  import { allComparators, allTemperatureLimits, allWeathers } from './WeatherTargetingEnumerations';

  export default {
    name: 'WeatherTargeting',
    components: {
      FormattedInput,
      FormLabel,
      InventoryMap,
      SelectableTiles,
    },
    props: {
      snowAccumulationCode: {
        type: String,
        default: 'SNOW_CM_LAST_12H_ABOVE',
      },
      snowAccumulationValue: {
        type: Number,
        default: 1,
      },
      rainAccumulationCode: {
        type: String,
        default: 'RAIN_MM_LAST_12H_ABOVE',
      },
      rainAccumulationValue: {
        type: Number,
        default: 1,
      },
      temperatureCode: {
        type: String,
        default: TargetingCriteriaType.DailyMaxAbove,
      },
      temperatureValue: {
        type: Number,
        default: 20,
      },
      selectedWeatherConditions: {
        type: Array,
        default: () => [],
      },
      isReadOnly: {
        type: Boolean,
        default: false,
      },
      targetingCriteria: {
        type: Array,
      },
      entity: {
        type: Object,
      },
      weatherTargetingCriteriaType: {
        type: String,
        default: TargetingCriteriaType.Weather,
      },
      temperatureTargetingCriteriaType: {
        type: String,
        default: TargetingCriteriaType.Temperature,
      },
      snowTargetingCriteriaType: {
        type: String,
        default: null,
      },
      rainTargetingCriteriaType: {
        type: String,
        default: null,
      },
      requiresLocation: {
        type: Boolean,
        default: false,
      },
      latitude: {
        type: Number,
        default: null,
      },
      longitude: {
        type: Number,
        default: null,
      },
    },
    data() {
      return {
        allComparators,
        allTemperatureLimits,
        allWeathers: allWeathers(),
        areWeatherConditionsEnabled: false,
        colors: Colors,
        enableWeatherConditions: false,
        enableWeatherTemperature: false,
        enableSnowAccumulation: false,
        enableRainAccumulation: false,
        i18n,
        internalSelectedWeatherConditions: null,
        internalTemperatureValue: null,
        internalTemperatureCode: null,
        internalRainAccumulationCode: null,
        internalSnowAccumulationCode: null,
        internalRainAccumulationValue: null,
        internalSnowAccumulationValue: null,
        internalLatitude: null,
        internalLongitude: null,
        messageForWeatherConditions: '',
        messageForTemperatureMatches: '',
        messageForSnowAccumulationMatches: '',
        messageForRainAccumulationMatches: '',
        rainAccumulationUnit: 'mm', // 'mm' or 'in'
        selectedTempComparator: null,
        selectedTempLimit: null,
        selectedRainAccumulationComparator: null,
        selectedSnowAccumulationComparator: null,
        snowAccumulationUnit: 'cm', // 'cm' or 'in'
        tempUnit: 'C', // C or F
      };
    },
    watch: {
      internalLongitude() {
        this.fireLongitudeChanged();
      },
      internalLatitude() {
        this.fireLatitudeChanged();
      },
      selectedTempLimit() {
        this.fireTemperatureCodeChange();
      },
      selectedTempComparator() {
        this.fireTemperatureCodeChange();
      },
      selectedSnowAccumulationComparator() {
        this.fireSnowAccumulationCodeChange();
      },
      selectedRainAccumulationComparator() {
        this.fireRainAccumulationCodeChange();
      },
      selectedWeatherConditions(val) {
        if (val != null) {
          this.enableWeatherConditions = true;
          this.internalSelectedWeatherConditions = this.allWeathers.filter((x) =>
            val.find((y) => y.localeCompare(x.id) === 0),
          );
        } else {
          this.enableWeatherConditions = false;
          this.internalSelectedWeatherConditions = null;
        }
      },
      temperatureCode() {
        this.updateTempFromCode();

        this.enableWeatherTemperature =
          this.temperatureValue != null && !Number.isNaN(this.temperatureValue) && this.temperatureCode != null;
      },
      temperatureValue() {
        if (this.internalTemperatureValue == null) {
          this.internalTemperatureValue = this.temperatureValue;
        }
      },
      internalTemperatureValue(val) {
        this.enableWeatherTemperature =
          this.temperatureValue != null && !Number.isNaN(this.temperatureValue) && this.temperatureCode != null;

        if (this.isReadOnly) {
          return;
        }

        if (Number.isNaN(val)) {
          this.$emit('temperatureValueChanged', null);
          return;
        }
        if (this.tempUnit === 'F') {
          this.$emit('temperatureValueChanged', fahrenheitToCelsius(val));
        } else {
          this.$emit('temperatureValueChanged', val);
        }
      },
      snowAccumulationCode() {
        this.updateSnowAccumulationFromCode();

        this.enableSnowAccumulation =
          this.snowAccumulationValue != null &&
          !Number.isNaN(this.snowAccumulationValue) &&
          this.snowAccumulationCode != null;
      },
      snowAccumulationValue() {
        if (this.internalSnowAccumulationValue == null) {
          this.internalSnowAccumulationValue = this.snowAccumulationValue;
        }
      },
      internalSnowAccumulationValue(val) {
        this.enableSnowAccumulation =
          this.snowAccumulationValue != null &&
          !Number.isNaN(this.snowAccumulationValue) &&
          this.snowAccumulationCode != null;

        if (this.isReadOnly) {
          return;
        }

        if (Number.isNaN(val)) {
          this.$emit('snowAccumulationValueChanged', null);
          return;
        }
        if (this.snowAccumulationUnit === 'in') {
          this.$emit('snowAccumulationValueChanged', inchesToCentimeters(val));
        } else {
          this.$emit('snowAccumulationValueChanged', val);
        }
      },
      rainAccumulationCode() {
        this.updateRainAccumulationFromCode();

        this.enableRainAccumulation =
          this.rainAccumulationValue != null &&
          !Number.isNaN(this.rainAccumulationValue) &&
          this.rainAccumulationCode != null;
      },
      rainAccumulationValue() {
        if (this.internalRainAccumulationValue == null) {
          this.internalRainAccumulationValue = this.rainAccumulationValue;
        }
      },
      internalRainAccumulationValue(val) {
        this.enableRainAccumulation =
          this.rainAccumulationValue != null &&
          !Number.isNaN(this.rainAccumulationValue) &&
          this.rainAccumulationCode != null;

        if (this.isReadOnly) {
          return;
        }

        if (Number.isNaN(val)) {
          this.$emit('rainAccumulationValueChanged', null);
          return;
        }
        if (this.rainAccumulationUnit === 'in') {
          this.$emit('rainAccumulationValueChanged', inchesToMillimeters(val));
        } else {
          this.$emit('rainAccumulationValueChanged', val);
        }
      },
      enableWeatherConditions(val) {
        if (val) {
          if (this.selectedWeatherConditions == null) {
            if (this.isReadOnly) {
              return;
            }
            this.$emit('selectedWeatherConditionsChanged', [...this.allWeathers.map((x) => x.id)]);
          }
        } else {
          this.internalSelectedWeatherConditions = null;

          if (this.isReadOnly) {
            return;
          }
          this.$emit('selectedWeatherConditionsChanged', null);
        }
        if (this.$refs.weatherConditionsSelectableTiles)
          this.$refs.weatherConditionsSelectableTiles.onContainerResized();
      },
      enableWeatherTemperature(val) {
        if (this.isReadOnly) {
          return;
        }

        if (val) {
          if (this.selectedTempLimit == null) {
            const code = `${this.allTemperatureLimits()[0].id}_${this.allComparators()[0].id}`;
            this.$emit('temperatureCodeChanged', code);
            this.$emit('temperatureValueChanged', 20);
          }
        } else {
          this.$emit('temperatureCodeChanged', null);
        }
      },
      enableSnowAccumulation(val) {
        if (this.isReadOnly) {
          return;
        }

        if (val) {
          if (this.selectedSnowAccumulationComparator == null) {
            const code = `SNOW_CM_LAST_12H_${this.allComparators()[0].id}`;
            this.$emit('snowAccumulationCodeChanged', code);
            this.$emit('snowAccumulationValueChanged', 1);
          }
        } else {
          this.$emit('snowAccumulationCodeChanged', null);
        }
      },
      enableRainAccumulation(val) {
        if (this.isReadOnly) {
          return;
        }

        if (val) {
          if (this.selectedRainAccumulationComparator == null) {
            const code = `RAIN_MM_LAST_12H_${this.allComparators()[0].id}`;
            this.$emit('rainAccumulationCodeChanged', code);
            this.$emit('rainAccumulationValueChanged', 1);
          }
        } else {
          this.$emit('rainAccumulationCodeChanged', null);
        }
      },
    },
    mounted() {
      this.enableWeatherConditions =
        this.selectedWeatherConditions != null && this.selectedWeatherConditions.length > 0;
      this.enableWeatherTemperature =
        this.temperatureValue != null && !Number.isNaN(this.temperatureValue) && this.temperatureCode != null;
      this.enableSnowAccumulation =
        this.snowAccumulationValue != null &&
        !Number.isNaN(this.snowAccumulationValue) &&
        this.snowAccumulationCode != null;
      this.enableRainAccumulation =
        this.rainAccumulationValue != null &&
        !Number.isNaN(this.rainAccumulationValue) &&
        this.rainAccumulationCode != null;

      this.updateTempFromCode();
      this.internalTemperatureValue = this.temperatureValue;
      this.updateSnowAccumulationFromCode();
      this.internalSnowAccumulationValue = this.snowAccumulationValue;
      this.updateRainAccumulationFromCode();
      this.internalRainAccumulationValue = this.rainAccumulationValue;

      this.internalLatitude = this.latitude;
      this.internalLongitude = this.longitude;

      if (LocalCache.get('temperatureUnit') != null) {
        if (LocalCache.get('temperatureUnit') === 'F') {
          this.changeTempUnit('F');
        }
      } else if (this.temperatureValue != null) {
        // Try to figure out if the temperature was previously entered as fahrenheit.
        if (celsiusToFahrenheit(this.temperatureValue) % 0 === 0) {
          this.changeTempUnit('F');
        }
      }

      if (LocalCache.get('snowAccumulationUnit') != null) {
        if (LocalCache.get('snowAccumulationUnit') === 'in') {
          this.changeSnowAccumulationUnit('in');
        }
      }

      if (LocalCache.get('rainAccumulationUnit') != null) {
        if (LocalCache.get('rainAccumulationUnit') === 'in') {
          this.changeRainAccumulationUnit('in');
        }
      }

      if (this.enableWeatherConditions) {
        this.internalSelectedWeatherConditions = this.allWeathers.filter((x) =>
          this.selectedWeatherConditions.find((y) => y.localeCompare(x.id) === 0),
        );
      }

      this.getMessageForTemperatureMatches();
      this.getMessageForWeatherConditions();
      this.getMessageForSnowAccumulationMatches();
      this.getMessageForRainAccumulationMatches();
    },
    methods: {
      capitalize(s) {
        return capitalize(s);
      },
      onWeatherSelectionChanged(x) {
        if (this.isReadOnly) {
          return;
        }
        this.$emit(
          'selectedWeatherConditionsChanged',
          x.map((y) => y.id),
        );
      },
      fireTemperatureCodeChange() {
        if (this.isReadOnly) {
          return;
        }
        if (this.selectedTempLimit != null && this.selectedTempComparator != null) {
          this.internalTemperatureCode = `${this.selectedTempLimit.id}_${this.selectedTempComparator.id}`;
          this.$emit('temperatureCodeChanged', this.internalTemperatureCode);
        } else {
          this.internalTemperatureCode = null;
        }
      },
      fireSnowAccumulationCodeChange() {
        if (this.isReadOnly) {
          return;
        }
        if (this.selectedSnowAccumulationComparator != null) {
          this.internalSnowAccumulationCode = `SNOW_CM_LAST_12H_${this.selectedSnowAccumulationComparator.id}`;
          this.$emit('snowAccumulationCodeChanged', this.internalSnowAccumulationCode);
        } else {
          this.internalSnowAccumulationCode = null;
        }
      },
      fireRainAccumulationCodeChange() {
        if (this.isReadOnly) {
          return;
        }
        if (this.selectedRainAccumulationComparator != null) {
          this.internalRainAccumulationCode = `RAIN_MM_LAST_12H_${this.selectedRainAccumulationComparator.id}`;
          this.$emit('rainAccumulationCodeChanged', this.internalRainAccumulationCode);
        } else {
          this.internalRainAccumulationCode = null;
        }
      },
      fireLatitudeChanged() {
        if (this.isReadOnly) {
          return;
        }
        if (this.internalLatitude != null) {
          const number = parseFloat(`${this.internalLatitude}`);
          this.$emit('latitudeChanged', Number.isNaN(number) ? null : number);
        }
      },
      fireLongitudeChanged() {
        if (this.isReadOnly) {
          return;
        }
        if (this.internalLongitude != null) {
          const number = parseFloat(`${this.internalLongitude}`);
          this.$emit('longitudeChanged', Number.isNaN(number) ? null : number);
        }
      },
      updateTempFromCode() {
        if (this.temperatureCode != null) {
          const codeFragments = this.temperatureCode.split('_');
          const allButLastFragments = codeFragments.slice(0, codeFragments.length - 1);
          const lastFragment = codeFragments[codeFragments.length - 1];
          const limitId = allButLastFragments.join('_');
          this.selectedTempLimit = this.allTemperatureLimits().find((x) => x.id === limitId);
          this.selectedTempComparator = this.allComparators().find((x) => x.id === lastFragment);
        }
      },
      updateSnowAccumulationFromCode() {
        if (this.snowAccumulationCode != null) {
          const codeFragments = this.snowAccumulationCode.split('_');
          const lastFragment = codeFragments[codeFragments.length - 1];
          this.selectedSnowAccumulationComparator = this.allComparators().find((x) => x.id === lastFragment);
        }
      },
      updateRainAccumulationFromCode() {
        if (this.rainAccumulationCode != null) {
          const codeFragments = this.rainAccumulationCode.split('_');
          const lastFragment = codeFragments[codeFragments.length - 1];
          this.selectedRainAccumulationComparator = this.allComparators().find((x) => x.id === lastFragment);
        }
      },
      validate() {
        if (this.isReadOnly) {
          return true;
        }
        const extras = {};
        if (this.enableWeatherConditions && this.internalSelectedWeatherConditions.length === 0) {
          extras.targetingCriteriaWeatherConditions = {
            message: i18n.global.t('messages.pleaseSelectAtLeastOneWeatherCondition'),
            preEvaluated: true,
          };
        }
        if (this.enableWeatherTemperature && this.internalTemperatureCode == null) {
          extras.targetingCriteriaTemperature = {
            message: i18n.global.t('messages.pleaseSelectATemperatureLimitAndComparator'),
            preEvaluated: true,
          };
        }
        if (this.enableWeatherTemperature && this.internalTemperatureValue == null) {
          extras.targetingCriteriaTemperature = {
            message: i18n.global.t('messages.pleaseSpecifyATargetTemperature'),
            preEvaluated: true,
          };
        }
        if (this.enableSnowAccumulation && this.internalSnowAccumulationCode == null) {
          extras.targetingCriteriaSnowAccumulation = {
            message: i18n.global.t('messages.pleaseSelectASnowAccumulationComparator'),
            preEvaluated: true,
          };
        }
        if (this.enableSnowAccumulation && this.internalSnowAccumulationValue == null) {
          extras.targetingCriteriaSnowAccumulation = {
            message: i18n.global.t('messages.pleaseSpecifyATargetSnowAccumulation'),
            preEvaluated: true,
          };
        }
        if (this.enableRainAccumulation && this.internalRainAccumulationCode == null) {
          extras.targetingCriteriaRainAccumulation = {
            message: i18n.global.t('messages.pleaseSelectARainAccumulationComparator'),
            preEvaluated: true,
          };
        }
        if (this.enableRainAccumulation && this.internalRainAccumulationValue == null) {
          extras.targetingCriteriaRainAccumulation = {
            message: i18n.global.t('messages.pleaseSpecifyATargetRainAccumulation'),
            preEvaluated: true,
          };
        }

        if (
          this.requiresLocation &&
          (this.internalLatitude < -90 ||
            this.internalLatitude > 90 ||
            this.internalLongitude == null ||
            this.internalLongitude < -180 ||
            this.internalLongitude > 180)
        ) {
          extras.targetingCriteriaRainAccumulation = {
            message: i18n.global.t('messages.pleaseSpecifyAValidWeatherDestinationLocation'),
            preEvaluated: true,
          };
        }

        return extras;
      },
      changeTempUnit(unit) {
        if (unit === this.tempUnit) {
          return;
        }
        LocalCache.set('temperatureUnit', unit);
        this.tempUnit = unit;
        if (this.internalTemperatureValue != null) {
          if (unit === 'C') {
            this.internalTemperatureValue = fahrenheitToCelsius(this.internalTemperatureValue);
          } else {
            this.internalTemperatureValue = celsiusToFahrenheit(this.internalTemperatureValue);
          }
        }
      },
      changeSnowAccumulationUnit(unit) {
        if (unit === this.snowAccumulationUnit) {
          return;
        }
        LocalCache.set('snowAccumulationUnit', unit);
        this.snowAccumulationUnit = unit;
        if (this.internalSnowAccumulationValue != null) {
          if (unit === 'cm') {
            this.internalSnowAccumulationValue = inchesToCentimeters(this.internalSnowAccumulationValue);
          } else {
            this.internalSnowAccumulationValue = centimetersToInches(this.internalSnowAccumulationValue);
          }
        }
        if (this.internalRainAccumulationValue != null) {
          if (unit === 'mm') {
            this.internalRainAccumulationValue = inchesToMillimeters(this.internalRainAccumulationValue);
          } else {
            this.internalRainAccumulationValue = millimetersToInches(this.internalRainAccumulationValue);
          }
        }
      },
      changeRainAccumulationUnit(unit) {
        if (unit === this.rainAccumulationUnit) {
          return;
        }
        LocalCache.set('rainAccumulationUnit', unit);
        this.rainAccumulationUnit = unit;
        if (this.internalRainAccumulationValue != null) {
          if (unit === 'mm') {
            this.internalRainAccumulationValue = inchesToMillimeters(this.internalRainAccumulationValue);
          } else {
            this.internalRainAccumulationValue = millimetersToInches(this.internalRainAccumulationValue, 2);
          }
        }
      },
      getMessageForWeatherConditions() {
        if (this.entity instanceof LineItem) {
          this.messageForWeatherConditions = i18n.global.t(
            'messages.thisLineItemWillBeActiveDuringTheFollowingWeatherConditions',
          );
        } else if (this.entity instanceof Creative) {
          this.messageForWeatherConditions = i18n.global.t(
            'messages.thisCreativeWillBeEligibleDuringTheFollowingForecastedWeatherConditions',
          );
        } else if (this.entity instanceof Package) {
          this.messageForWeatherConditions = i18n.global.t(
            'messages.thisDealWillBeActiveDuringTheFollowingForecastedWeatherConditions',
          );
        }
      },
      getMessageForTemperatureMatches() {
        if (this.entity instanceof LineItem) {
          this.messageForTemperatureMatches = i18n.global.t(
            'messages.thisLineItemWillBeActiveWhenTheTemperatureMatchesTheExpressionBelow',
          );
        } else if (this.entity instanceof Creative) {
          this.messageForTemperatureMatches = i18n.global.t(
            'messages.thisCreativeWillBeEligibleWhenTheForecastedTemperatureMatchesTheExpressionBelow',
          );
        } else if (this.entity instanceof Package) {
          this.messageForTemperatureMatches = i18n.global.t(
            'messages.thisDealWillBeActiveWhenTheForecastedTemperatureMatchesTheExpressionBelow',
          );
        }
      },
      getMessageForSnowAccumulationMatches() {
        if (this.entity instanceof LineItem) {
          this.messageForSnowAccumulationMatches = i18n.global.t(
            'messages.thisLineItemWillBeActiveWhenTheSnowAccumulationMatchesTheExpressionBelow',
          );
        } else if (this.entity instanceof Creative) {
          this.messageForSnowAccumulationMatches = i18n.global.t(
            'messages.thisCreativeWillBeEligibleWhenTheSnowAccumulationForTheLast12HoursMatchesTheExpressionBelow',
          );
        } else if (this.entity instanceof Package) {
          this.messageForSnowAccumulationMatches = i18n.global.t(
            'messages.thisDealWillBeActiveWhenTheForecastedSnowAccumulationMatchesTheExpressionBelow',
          );
        }
      },
      getMessageForRainAccumulationMatches() {
        if (this.entity instanceof LineItem) {
          this.messageForRainAccumulationMatches = i18n.global.t(
            'messages.thisLineItemWillBeActiveWhenTheRainAccumulationMatchesTheExpressionBelow',
          );
        } else if (this.entity instanceof Creative) {
          this.messageForRainAccumulationMatches = i18n.global.t(
            'messages.thisCreativeWillBeEligibleWhenTheRainAccumulationForTheLast12HoursMatchesTheExpressionBelow',
          );
        } else if (this.entity instanceof Package) {
          this.messageForRainAccumulationMatches = i18n.global.t(
            'messages.thisDealWillBeActiveWhenTheForecastedRainAccumulationMatchesTheExpressionBelow',
          );
        }
      },
      onMapPointClicked(e) {
        [this.internalLongitude, this.internalLatitude] = e;
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  .temperature-controls-container,
  .accumulation-controls-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    > * {
      margin-right: 1.2rem;
      margin-bottom: 0.8rem;

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  .accumulation-controls-container {
    .formatted-input {
      min-width: 12rem;
      max-width: 12rem;
    }
  }

  #weather_temp_limit {
    .multiselect {
      width: 26rem;
    }
  }

  #weather_temp_comparator {
    .multiselect {
      width: 13rem;
    }
  }

  #weather_temp_target {
    width: 10rem;
  }

  @media (max-width: 991px) {
    #weather_temp_limit {
      .multiselect {
        width: 100%;
      }
    }
  }

  .coordinates-picker {
    padding: 1.6rem;
  }
</style>
